import { FC, useRef, useState } from "react";
import { Field, Form } from "react-final-form";
import { Button } from "primereact/button";
import {
  REIAccountUpdateDisclaimer,
  REIDialoge,
  REIInputText,
  REIProgressSpinner,
  REIToastContainer,
} from "../../components/ui";
import { ApiEndPoints } from "../../utils/endpoints";
import { EMAIL_REGEX, MEMBER_KEY } from "../../utils/constants";
import { httpPost } from "../../services/httpService";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";

const ChangeEmail: FC<{ emailId: string }> = ({ emailId }) => {
  const [loading, setLoading] = useState(false);
  const memberId = localStorage.getItem(MEMBER_KEY);
  const toastRef = useRef<Toast>(null);
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);

  const onSubmit = async (data, form) => {
    const userdetail = {
      currentEmailAddress: data.currentEmail.trim(),
      newEmailAddress: data.newEmail.trim(),
      confirmEmailAddress: data.confirmEmail.trim(),
      memberId: memberId,
    };

    if (data.newEmail.trim() !== data.confirmEmail.trim()) {
      if (toastRef.current) {
        toastRef.current.show({
          severity: "error",
          summary: "Error",
          detail: "New Email and Confirm Email do not match.",
          life: 3000,
        });
      }
      return;
    }

    if (data.currentEmail.trim() === data.newEmail.trim()) {
      if (toastRef.current) {
        toastRef.current.show({
          severity: "error",
          summary: "Error",
          detail: "New Email and Current Email cannot be same.",
          life: 3000,
        });
      }
      return;
    }

    await httpPost(ApiEndPoints.myaccount + "/ChangeHubspotPrimaryEmail", userdetail).then((response) => {
      if (response.status == 200 && response.data != false) {
        UpdateEmailOnExternalSystems(data);
      } else if (response.status == 200 && response.data == false) {
        toastRef.current.show({
          severity: "error",
          summary: "",
          detail: "An error occurred while updating the primary email. New email address already exists!",
          life: 3000,
        });
      } else {
        toastRef.current.show({
          severity: "error",
          summary: "Error",
          detail: response?.error,
          life: 3000,
        });
        form.restart();
        setLoading(false);
      }
    });
  };

  const renderSuccessMessage = (
    <div className="text-sm">
      Record has been successfuly updated! <br />
      Pls. check your email, if you havent received a confirmation email.
      <br />
      It means there is an error behind the scene. Pls contact Dev team."
    </div>
  );

  const UpdateEmailOnExternalSystems = async (data: any) => {
    setLoading(true);
    const userdetails = {
      CurrentEmailAddress: String(data.currentEmail).toLowerCase().trim(),
      NewEmailAddress: data.newEmail.trim(),
    };
    await httpPost(ApiEndPoints.changeEmail + "/changeemailaddress", userdetails).then((result) => {
      if (result.status == 200) {
        setShowDialog(true);
      }
    });
  };

  const validateEmailFormat = (value) => {
    if (!value) {
      return "Required";
    }
    const isEmailValid = EMAIL_REGEX.test(value);
    return isEmailValid ? undefined : "Invalid Email";
  };

  return (
    <>
      <REIToastContainer toastReference={toastRef} />
      {loading && <REIProgressSpinner />}
      <REIDialoge
        header="Success Message"
        visible={showDialog}
        position="center"
        contentComponent={renderSuccessMessage}
        handleEditClick={() => {
          setLoading(false);
          navigate("/login");
        }}
      ></REIDialoge>
      {!loading && (
        <Form
          onSubmit={onSubmit}
          initialValues={{ currentEmail: emailId, newEmail: "", confirmEmail: "" }}
          render={({ handleSubmit, submitting, valid, form, errors }) => (
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col gap-4 gap-x-6">
                <div className="flex flex-grow gap-4">
                  <div className="mt-3 self-center">
                    <label htmlFor="currentEmail">Current Email Address</label>
                  </div>
                  <Field name="currentEmail">
                    {({ input, meta }) => (
                      <div className="mt-3">
                        <REIInputText
                          id="cmail-txt-current"
                          className="w-full lg:w-72"
                          {...input}
                          type="text"
                          placeholder="New Email"
                          value={emailId}
                          disabled={true}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                <div className="flex gap-4">
                  <div className="mt-3 self-center">
                    <label htmlFor="newEmail">New Email Address</label>
                  </div>
                  <Field name="newEmail" validate={validateEmailFormat}>
                    {({ input, meta }) => (
                      <div className="mt-3 ml-6">
                        <REIInputText
                          id="cmail-txt-new"
                          {...input}
                          className={`w-full lg:w-72 ${meta.touched && meta.error ? "border-red-500" : ""}`}
                          type="text"
                          placeholder="New Email"
                        />
                        {meta.touched && meta.error && <span style={{ color: "red" }}>{meta.error}</span>}
                      </div>
                    )}
                  </Field>
                </div>
                <div className="flex gap-4">
                  <div className="mt-3 self-center">
                    <label htmlFor="confirmEmail">Confirm Email Address</label>
                  </div>
                  <Field
                    name="confirmEmail"
                    validate={(value) => {
                      if (!value) {
                        return "Required";
                      }
                      return undefined;
                    }}
                  >
                    {({ input, meta }) => (
                      <div className="mt-3">
                        <REIInputText
                          id="cmail-txt-confirm"
                          {...input}
                          className={`w-full lg:w-72 ${meta.touched && meta.error ? "border-red-500" : ""}`}
                          type="text"
                          placeholder="Confirm New Email"
                        />
                        {meta.touched && meta.error && <span style={{ color: "red" }}>{meta.error}</span>}
                      </div>
                    )}
                  </Field>
                </div>
                <REIAccountUpdateDisclaimer />
                <div className="flex gap-4">
                  <Button
                    id="cmail-btn-update"
                    data-testid="cmail-btn-update"
                    type="submit"
                    disabled={submitting || !valid}
                    label="Update"
                    loading={loading}
                  />
                  <Button
                    id="cmail-btn-reset"
                    data-testid="cmail-btn-reset"
                    type="reset"
                    label="Reset"
                    onClick={() => form.reset()}
                  />
                </div>
              </div>
            </form>
          )}
        />
      )}
    </>
  );
};

export default ChangeEmail;
